angular.module("LeasePilot").factory("FieldConstraintsService", function() {
  var MAX_ALLOWED_VALUES = 50;
  var SECTION_HINTS = [
    {
      prefixes: [
        "hasRenewalOption",
        "renewal",
        "renewalInfo",
        "terms"
      ],
      sectionCategory: "renewal-i-icon",
      parentAttribute: "Renewal"
    },
    {
      prefixes: [
        "hasParking",
        "parking",
        "reservedParking",
        "unreservedParking",
        "exclusiveParking"
      ],
      sectionCategory: "parking-i-icon",
      parentAttribute: "Right - Parking"
    },
    {
      prefixes: [
        "tiAllowance",
        "tenantImprovement",
        "tenantImprovements",
        "premisesImprovements"
      ],
      sectionCategory: "tenant-improvement-i-icon",
      parentAttribute: "Tenant Improvements"
    },
    {
      prefixes: [
        "additionalRent",
        "additionalRentType",
        "hasPercentageRent",
        "percentageRent",
        "pctRent",
        "hasPromotionFund",
        "promotionFund"
      ],
      sectionCategory: "additional-expenses-i-icon",
      parentAttribute: "Additional Expenses"
    },
    {
      prefixes: [
        "commonAreaReimbursement",
        "insuranceReimbursement",
        "realEstateReimbursement",
        "taxBaseYear",
        "expenseBaseYear",
        "officeMaintenanceBaseYear",
        "proportionateShare",
        "opEx",
        "fixedCam",
        "hasManagementFee",
        "managementFee"
      ],
      sectionCategory: "reimbursements-opex-i-icon",
      parentAttribute: "Reimbursements"
    },
    {
      prefixes: [
        "baseRent",
        "annualIncrease",
        "rentPeriods",
        "rentInputType"
      ],
      sectionCategory: "base-rent-i-icon",
      parentAttribute: "Base Rent"
    },
    {
      prefixes: [
        "leaseYears",
        "leaseMonths",
        "termNumber",
        "commencementDate",
        "expirationDate",
        "hasFreeRent",
        "freeRent"
      ],
      sectionCategory: "term-i-icon",
      parentAttribute: "Term"
    }
  ];

  function normalizePath(path) {
    if (!path) return null;

    var normalizedPath = path.trim();
    return normalizedPath || null;
  }

  // Used only for deduping synthetic constraints against live-DOM ones in
  // collect(). Mirrors deal_terms_import_service.normalizeConstraintPath so
  // both ends compare like-for-like.
  function normalizePathForDedupe(path) {
    if (!path) return "";

    return String(path)
      .replace(/^lease\./, "")
      .replace(/\[\$parent\.\$index\]/g, "[0]")
      .replace(/\[\$index\]/g, "[0]")
      .replace(/\[\d+\]/g, "[0]")
      .trim();
  }

  function categoryForPath(path) {
    return path.replace(/^lease\./, "");
  }

  function pathMatchesPrefix(path, prefix) {
    return path.indexOf(prefix) === 0;
  }

  function sectionHintForPath(path) {
    var category = categoryForPath(path);

    for (var i = 0; i < SECTION_HINTS.length; i++) {
      var hint = SECTION_HINTS[i];

      for (var j = 0; j < hint.prefixes.length; j++) {
        if (pathMatchesPrefix(category, hint.prefixes[j])) {
          return {
            sectionCategory: hint.sectionCategory,
            parentAttribute: hint.parentAttribute
          };
        }
      }
    }

    return null;
  }

  function normalizeLabel(label) {
    if (!label) return null;

    var normalizedLabel = label.replace(/\s+/g, " ").trim();
    if (!normalizedLabel) return null;

    if (normalizedLabel.length > 120) {
      return normalizedLabel.substring(0, 117) + "...";
    }

    return normalizedLabel;
  }

  function findEditorLabelForPath(path) {
    var labels = document.querySelectorAll("editor-label[for]");

    for (var i = 0; i < labels.length; i++) {
      if (labels[i].getAttribute("for") === path) {
        return labels[i];
      }
    }

    return null;
  }

  function extractUnit(control) {
    if (!control) return null;

    // Form templates render numeric units like "$/Sf", "%", "months", "Day(s)"
    // inside a `<div class="unit">...</div>` sibling of the input. Capture
    // that text so the extractor can distinguish "$37.00 PSF" from
    // "Total TI: $66,600.00" when populating a per-square-foot field.
    var unitElement = control.querySelector(".unit");
    if (!unitElement) return null;

    var text = (unitElement.textContent || "").replace(/\s+/g, " ").trim();
    if (!text) return null;

    return text.length > 40 ? text.slice(0, 40) : text;
  }

  function extractLabel(control, path) {
    if (!control) return null;

    var editorLabel =
      control.querySelector("editor-label") || findEditorLabelForPath(path);
    var label = normalizeLabel(editorLabel && editorLabel.textContent);
    if (label) return label;

    var nativeLabel = control.closest && control.closest("label");
    label = normalizeLabel(nativeLabel && nativeLabel.textContent);
    if (label) return label;

    label = normalizeLabel(control.getAttribute("label"));
    if (label) return label;

    label = normalizeLabel(control.getAttribute("aria-label"));
    if (label) return label;

    var labelledElement = control.querySelector("[aria-label]");
    return normalizeLabel(
      labelledElement && labelledElement.getAttribute("aria-label")
    );
  }

  function detectControlType(control) {
    if (!control) return "unknown";

    if (control.querySelector("textarea")) {
      return "textarea";
    }

    if (control.querySelector("md-select, select")) {
      return "select";
    }

    if (control.querySelector("md-radio-group, input[type='radio']")) {
      return "radio";
    }

    if (control.querySelector("md-checkbox, input[type='checkbox']")) {
      return "checkbox";
    }

    var input = control.querySelector("input");
    if (input) {
      var inputType = (input.getAttribute("type") || "text").toLowerCase();

      if (inputType === "number") {
        return "number";
      }

      if (inputType === "date") {
        return "date";
      }

      if (inputType === "checkbox") {
        return "checkbox";
      }

      if (inputType === "radio") {
        return "radio";
      }

      if (inputType === "text" || inputType === "") {
        return "text";
      }
    }

    return "unknown";
  }

  function isElementVisible(element) {
    if (!element) return false;

    var current = element;
    while (current && current.nodeType === Node.ELEMENT_NODE) {
      if (
        current.hasAttribute("hidden") ||
        current.getAttribute("aria-hidden") === "true" ||
        current.classList.contains("ng-hide") ||
        current.classList.contains("ng-cloak")
      ) {
        return false;
      }

      var style = window.getComputedStyle(current);
      if (
        style.display === "none" ||
        style.visibility === "hidden" ||
        style.visibility === "collapse"
      ) {
        return false;
      }

      current = current.parentElement;
    }

    return document.documentElement.contains(element);
  }

  function isElementEnabled(element) {
    if (!element) return false;

    var disabledSelector =
      "[disabled], [readonly], [aria-disabled='true'], .disabled, .readonly, .read-only, .ng-disabled, .md-disabled";

    if (element.closest && element.closest(disabledSelector)) {
      return false;
    }

    var disabledChild = element.querySelector(disabledSelector);
    return !disabledChild;
  }

  function normalizeOptionLabel(label) {
    return normalizeLabel(label);
  }

  function optionValueFromElement(option) {
    if (!option) return null;

    var value =
      option.getAttribute("value") ||
      option.getAttribute("ng-value") ||
      option.getAttribute("data-ng-value") ||
      option.getAttribute("data-value");

    if (value === null || value === undefined) {
      value = option.value;
    }

    if (value === null || value === undefined) return null;

    var normalizedValue = String(value).trim();
    return normalizedValue || null;
  }

  function parseAngularOptionValue(rawValue) {
    if (rawValue === null || rawValue === undefined) return null;

    var value = String(rawValue).trim();
    if (!value) return null;

    var match = value.match(/^(number|string|boolean|object):(.+)$/);
    if (!match) {
      return { value: value };
    }

    var valueType = match[1];
    var typedValue = match[2];
    var parsed = {
      value: typedValue,
      rawValue: value,
      angularType: valueType
    };

    if (valueType === "number") {
      var numberValue = Number(typedValue);
      if (!isNaN(numberValue)) {
        parsed.value = numberValue;
      }
    } else if (valueType === "boolean") {
      if (typedValue === "true") {
        parsed.value = true;
      } else if (typedValue === "false") {
        parsed.value = false;
      }
    } else if (valueType === "object") {
      parsed.value = value;
      parsed.warning = "object_reference";
    }

    return parsed;
  }

  function optionEntryFromElement(option, labelText) {
    var rawValue = optionValueFromElement(option);
    var parsedValue = parseAngularOptionValue(rawValue);
    if (!parsedValue) return null;

    if (
      (option.hasAttribute("data-ng-value") || option.hasAttribute("ng-value")) &&
      (rawValue === "true" || rawValue === "false")
    ) {
      parsedValue.value = rawValue === "true";
      parsedValue.rawValue = rawValue;
      parsedValue.angularType = "boolean";
    }

    var label = normalizeOptionLabel(labelText) || String(parsedValue.value);
    var entry = {
      value: parsedValue.value,
      label: label
    };

    if (parsedValue.rawValue) {
      entry.rawValue = parsedValue.rawValue;
    }

    if (parsedValue.angularType) {
      entry.angularType = parsedValue.angularType;
    }

    if (parsedValue.warning) {
      entry.warning = parsedValue.warning;
    }

    return entry;
  }

  function extractNativeSelectValues(select) {
    var values = [];
    if (!select) return values;

    Array.prototype.forEach.call(select.querySelectorAll("option"), function(
      option
    ) {
      var entry = optionEntryFromElement(
        option,
        option.getAttribute("label") || option.textContent
      );
      if (!entry) return;

      values.push(entry);
    });

    return values;
  }

  function extractMdSelectValues(mdSelect) {
    var values = [];
    if (!mdSelect) return values;

    Array.prototype.forEach.call(mdSelect.querySelectorAll("md-option"), function(
      option
    ) {
      var entry = optionEntryFromElement(option, option.textContent);
      if (!entry) return;

      values.push(entry);
    });

    return values;
  }

  function extractMdRadioValues(radioGroup) {
    var values = [];
    if (!radioGroup) return values;

    Array.prototype.forEach.call(
      radioGroup.querySelectorAll("md-radio-button"),
      function(option) {
        var entry = optionEntryFromElement(option, option.textContent);
        if (!entry) return;

        values.push(entry);
      }
    );

    return values;
  }

  function extractNativeRadioValues(control) {
    var values = [];
    if (!control) return values;

    var radios = control.querySelectorAll("input[type='radio']");

    Array.prototype.forEach.call(radios, function(radio) {
      var label = null;
      if (radio.id) {
        var labelElement = document.querySelector(
          "label[for='" + CSS.escape(radio.id) + "']"
        );
        label = normalizeOptionLabel(labelElement && labelElement.textContent);
      }

      if (!label && radio.closest) {
        label = normalizeOptionLabel(
          radio.closest("label") && radio.closest("label").textContent
        );
      }

      var entry = optionEntryFromElement(radio, label);
      if (!entry) return;

      values.push(entry);
    });

    return values;
  }

  function extractAllowedValues(control, type) {
    if (type !== "select" && type !== "radio") return undefined;

    if (type === "radio") {
      var radioGroup = control.querySelector("md-radio-group");
      var radioValues = radioGroup
        ? extractMdRadioValues(radioGroup)
        : extractNativeRadioValues(control);

      return radioValues.length > 0 ? radioValues : undefined;
    }

    var mdSelect = control.querySelector("md-select");
    var allowedValues = mdSelect
      ? extractMdSelectValues(mdSelect)
      : extractNativeSelectValues(control.querySelector("select"));

    return allowedValues.length > 0 ? allowedValues : undefined;
  }

  function pruneAllowedValues(constraint, allowedValues) {
    if (!allowedValues || allowedValues.length === 0) return;

    if (allowedValues.length > MAX_ALLOWED_VALUES) {
      constraint.allowedValues = allowedValues.slice(0, MAX_ALLOWED_VALUES);
      constraint._truncated = true;
      return;
    }

    constraint.allowedValues = allowedValues;
  }

  function collect() {
    var controls = document.querySelectorAll("editor-control[for]");
    var constraints = [];
    var seenNormalizedPaths = new Set();

    controls.forEach(function(control) {
      var path = normalizePath(control.getAttribute("for"));
      if (!path) return;
      var type = detectControlType(control);
      var sectionHint = sectionHintForPath(path);

      var constraint = {
        path: path,
        category: categoryForPath(path),
        label: extractLabel(control, path),
        type: type,
        visible: isElementVisible(control),
        enabled: isElementEnabled(control)
      };

      var unit = extractUnit(control);
      if (unit) {
        constraint.unit = unit;
      }

      if (sectionHint) {
        constraint.sectionCategory = sectionHint.sectionCategory;
        constraint.parentAttribute = sectionHint.parentAttribute;
      }

      var allowedValues = extractAllowedValues(control, type);
      pruneAllowedValues(constraint, allowedValues);

      constraints.push(constraint);
      seenNormalizedPaths.add(normalizePathForDedupe(path));
    });

    // Phase 2: detect ng-repeat directives the active form actually renders
    // — either as live elements (when the bound array has items) or as
    // comment placeholders (when the bound array is currently empty). Emit
    // one ARRAY MARKER constraint per unique active array path so values
    // for currently-empty arrays still flow through Filter 1 in
    // DealTermsImportService (which treats arrayMarker constraints as a
    // fallback when no direct leaf-path constraint matches).
    //
    // We rely on the rendered DOM rather than scanning ng-template script
    // tags so the synthesized constraints stay scoped to arrays the
    // active form is actually using.
    var syntheticCount = 0;
    var ngRepeatBindingRegex = /\s+in\s+(lease\.[A-Za-z0-9_$.[\]]+)/;

    function emitArrayMarkerConstraint(rawArrayPath) {
      if (!rawArrayPath) return;
      var trimmed = String(rawArrayPath).trim();
      if (!trimmed) return;
      var dedupeKey = normalizePathForDedupe(trimmed);
      if (!dedupeKey || seenNormalizedPaths.has(dedupeKey)) return;

      constraints.push({
        path: trimmed,
        category: categoryForPath(trimmed),
        type: "array",
        visible: true,
        enabled: true,
        synthetic: true,
        arrayMarker: true
      });
      seenNormalizedPaths.add(dedupeKey);
      syntheticCount++;
    }

    // Live ng-repeat elements — present when the bound array has items.
    var liveRepeatNodes = document.querySelectorAll(
      "[ng-repeat], [data-ng-repeat]"
    );
    liveRepeatNodes.forEach(function(node) {
      var expr =
        node.getAttribute("ng-repeat") ||
        node.getAttribute("data-ng-repeat");
      if (!expr) return;
      var bindingMatch = expr.match(ngRepeatBindingRegex);
      if (bindingMatch) emitArrayMarkerConstraint(bindingMatch[1]);
    });

    // Comment placeholders Angular leaves when ng-repeat's array is empty.
    // Format: " ngRepeat: <itemVar> in lease.<path> ".
    if (typeof document.createTreeWalker === "function") {
      var commentRoot = document.body || document.documentElement;
      if (commentRoot) {
        var commentWalker = document.createTreeWalker(
          commentRoot,
          NodeFilter.SHOW_COMMENT,
          null,
          false
        );
        var commentNode;
        while ((commentNode = commentWalker.nextNode())) {
          var text = commentNode.nodeValue || "";
          var placeholderMatch = text.match(
            /ngRepeat:\s*\S+\s+in\s+(lease\.[A-Za-z0-9_$.[\]]+)/
          );
          if (placeholderMatch) {
            emitArrayMarkerConstraint(placeholderMatch[1]);
          }
        }
      }
    }

    if (window.isDebug && syntheticCount > 0) {
      console.log(
        "FieldConstraintsService synthesized " +
          syntheticCount +
          " array marker constraints from rendered ng-repeat directives"
      );
    }

    if (window.isDebug) {
      var leasePrefixedCategoryCount = constraints.filter(function(constraint) {
        return (
          constraint.category && constraint.category.indexOf("lease.") === 0
        );
      }).length;
      var labeledConstraintCount = constraints.filter(function(constraint) {
        return !!constraint.label;
      }).length;
      var hiddenConstraintCount = constraints.filter(function(constraint) {
        return !constraint.visible;
      }).length;
      var disabledConstraintCount = constraints.filter(function(constraint) {
        return !constraint.enabled;
      }).length;
      var constraintsWithAllowedValuesCount = constraints.filter(function(
        constraint
      ) {
        return constraint.allowedValues && constraint.allowedValues.length > 0;
      }).length;
      var truncatedConstraintCount = constraints.filter(function(constraint) {
        return !!constraint._truncated;
      }).length;
      var sectionHintCount = constraints.filter(function(constraint) {
        return !!constraint.sectionCategory;
      }).length;
      var payloadSizeBytes = JSON.stringify(constraints).length;

      console.log(
        "FieldConstraintsService collected constraints:",
        constraints.length,
        "lease-prefixed categories:",
        leasePrefixedCategoryCount,
        "with labels:",
        labeledConstraintCount,
        "hidden:",
        hiddenConstraintCount,
        "disabled:",
        disabledConstraintCount,
        "with allowed values:",
        constraintsWithAllowedValuesCount,
        "truncated:",
        truncatedConstraintCount,
        "with section hints:",
        sectionHintCount,
        "payload bytes:",
        payloadSizeBytes
      );
    }

    return constraints;
  }

  return {
    collect: collect
  };
});
